import React, { useEffect, useState } from "react";
import RightDrawer from "../../shared/components/RightDrawer";

const DEFAULT_DEPARTMENTS = [
  { uuid: "dept_eng", name: "Engineering" },
  { uuid: "dept_des", name: "Design & UI/UX" },
  { uuid: "dept_hr", name: "Human Resources" },
  { uuid: "dept_mgmt", name: "Management & Strategy" },
  { uuid: "dept_qa", name: "Quality Assurance (QA)" },
  { uuid: "dept_devops", name: "DevOps & Infrastructure" },
  { uuid: "dept_sales", name: "Sales & Marketing" },
  { uuid: "dept_ops", name: "Finance & Operations" },
  { uuid: "dept_prod", name: "Product Management" },
  { uuid: "dept_support", name: "Customer Support" },
];

const DEFAULT_EMPLOYMENT_TYPES = [
  { uuid: "type_ft", type_name: "Full-Time Permanent" },
  { uuid: "type_intern", type_name: "Internship" },
  { uuid: "type_prob", type_name: "Probationary" },
  { uuid: "type_contract", type_name: "Fixed-Term Contract" },
  { uuid: "type_pt", type_name: "Part-Time" },
  { uuid: "type_consultant", type_name: "Consultant" },
  { uuid: "type_free", type_name: "Freelancer" },
];

const DEFAULT_WORK_SCHEDULES = [
  { uuid: "sched_gen", schedule_name: "General Shift (09:00 - 18:00)" },
  { uuid: "sched_morn", schedule_name: "Morning Shift (07:00 - 16:00)" },
  { uuid: "sched_eve", schedule_name: "Evening Shift (14:00 - 23:00)" },
  { uuid: "sched_night", schedule_name: "Night Shift (22:00 - 07:00)" },
  { uuid: "sched_flex", schedule_name: "Flexible Working Hours" },
];

const EditEmployeeDrawer = ({
  isOpen = false,
  onClose,
  employee,
  onSave,
  departments = [],
  employmentTypes = [],
  workSchedules = [],
}) => {
  // Ensure DB option lists contain full selectable options
  const dbDepartments = React.useMemo(() => {
    const list = Array.isArray(departments) && departments.length > 0 ? [...departments] : [...DEFAULT_DEPARTMENTS];
    if (employee?.department_name && employee.department_name !== "—") {
      const exists = list.some(
        (d) =>
          d.name === employee.department_name ||
          d.department_name === employee.department_name ||
          d.uuid === employee.department_uuid
      );
      if (!exists) {
        list.unshift({
          uuid: employee.department_uuid || `dept_${employee.department_name}`,
          name: employee.department_name,
        });
      }
    }
    return list;
  }, [departments, employee]);

  const dbEmploymentTypes = React.useMemo(() => {
    const list = Array.isArray(employmentTypes) && employmentTypes.length > 0 ? [...employmentTypes] : [...DEFAULT_EMPLOYMENT_TYPES];
    const targetType = employee?.employment_type_name || employee?.contract_type;
    if (targetType && targetType !== "—") {
      const exists = list.some(
        (t) =>
          t.type_name === targetType ||
          t.name === targetType ||
          t.uuid === employee?.employment_type_uuid
      );
      if (!exists) {
        list.unshift({
          uuid: employee?.employment_type_uuid || `type_${targetType}`,
          type_name: targetType,
        });
      }
    }
    return list;
  }, [employmentTypes, employee]);

  const dbWorkSchedules = React.useMemo(() => {
    const list = Array.isArray(workSchedules) && workSchedules.length > 0 ? [...workSchedules] : [...DEFAULT_WORK_SCHEDULES];
    if (employee?.work_schedule_name && employee.work_schedule_name !== "—") {
      const exists = list.some(
        (s) =>
          s.schedule_name === employee.work_schedule_name ||
          s.name === employee.work_schedule_name ||
          s.uuid === employee.work_schedule_uuid
      );
      if (!exists) {
        list.unshift({
          uuid: employee.work_schedule_uuid || `sched_${employee.work_schedule_name}`,
          schedule_name: employee.work_schedule_name,
        });
      }
    }
    return list;
  }, [workSchedules, employee]);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    designation: "",
    employee_id: "",
    department_uuid: "",
    employment_type_uuid: "",
    work_schedule_uuid: "",
    status: "Active",
    joining_date: "",
  });

  useEffect(() => {
    if (employee) {
      const deptMatch = dbDepartments.find(
        (d) =>
          d.uuid === employee.department_uuid ||
          d.department_name === employee.department_name ||
          d.name === employee.department_name
      );
      const typeMatch = dbEmploymentTypes.find(
        (t) =>
          t.uuid === employee.employment_type_uuid ||
          t.type_name === employee.employment_type_name ||
          t.name === employee.employment_type_name ||
          t.type_name === employee.contract_type
      );
      const schedMatch = dbWorkSchedules.find(
        (s) =>
          s.uuid === employee.work_schedule_uuid ||
          s.schedule_name === employee.work_schedule_name ||
          s.name === employee.work_schedule_name
      );

      const rawStatus = employee.status || "Active";
      const normalizedStatus =
        rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();

      setFormData({
        full_name: employee.full_name || "",
        email: employee.email || "",
        designation: employee.designation || "",
        employee_id: employee.employee_id || "",
        department_uuid: deptMatch?.uuid || employee.department_uuid || (dbDepartments[0]?.uuid || ""),
        employment_type_uuid: typeMatch?.uuid || employee.employment_type_uuid || (dbEmploymentTypes[0]?.uuid || ""),
        work_schedule_uuid: schedMatch?.uuid || employee.work_schedule_uuid || (dbWorkSchedules[0]?.uuid || ""),
        status: normalizedStatus,
        joining_date: employee.joining_date ? employee.joining_date.slice(0, 10) : "",
      });
    } else {
      setFormData({
        full_name: "",
        email: "",
        designation: "",
        employee_id: "",
        department_uuid: dbDepartments[0]?.uuid || "",
        employment_type_uuid: dbEmploymentTypes[0]?.uuid || "",
        work_schedule_uuid: dbWorkSchedules[0]?.uuid || "",
        status: "Active",
        joining_date: new Date().toISOString().slice(0, 10),
      });
    }
  }, [employee, isOpen, dbDepartments, dbEmploymentTypes, dbWorkSchedules]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.email) {
      alert("Name and Email are required.");
      return;
    }

    const dept = dbDepartments.find((d) => d.uuid === formData.department_uuid);
    const type = dbEmploymentTypes.find((t) => t.uuid === formData.employment_type_uuid);
    const sched = dbWorkSchedules.find((s) => s.uuid === formData.work_schedule_uuid);

    const enrichedPayload = {
      ...formData,
      department_name: dept ? dept.name || dept.department_name : (formData.department_uuid || ""),
      employment_type_name: type ? type.type_name || type.name : (formData.employment_type_uuid || ""),
      work_schedule_name: sched ? sched.schedule_name || sched.name : (formData.work_schedule_uuid || ""),
      contract_type: type ? type.type_name || type.name : "Permanent Agreement",
    };

    onSave?.(enrichedPayload, employee?.uuid || employee?.employee_id);
  };

  return (
    <RightDrawer
      open={isOpen}
      onClose={onClose}
      title={employee ? "Edit Employee" : "Add Employee"}
      description="Fill in the details to update the employment record."
      width="460px"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="h-10 rounded-lg bg-[#147db5] px-4 text-sm font-medium text-white hover:bg-[#116d9d]"
          >
            Save Changes
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4 p-6">
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Full Name *</label>
          <input
            type="text"
            required
            value={formData.full_name}
            onChange={(e) => handleChange("full_name", e.target.value)}
            placeholder="John Doe"
            disabled={!!employee}
            className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#147db5] focus:ring-2 focus:ring-[#147db5]/10 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Email Address *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder="john.doe@company.com"
            disabled={!!employee}
            className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#147db5] focus:ring-2 focus:ring-[#147db5]/10 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Employee ID</label>
            <input
              type="text"
              value={formData.employee_id}
              onChange={(e) => handleChange("employee_id", e.target.value)}
              placeholder="EMP123"
              disabled={!!employee}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#147db5] focus:ring-2 focus:ring-[#147db5]/10 disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Designation</label>
            <input
              type="text"
              value={formData.designation}
              onChange={(e) => handleChange("designation", e.target.value)}
              placeholder="Software Engineer"
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#147db5] focus:ring-2 focus:ring-[#147db5]/10"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Department</label>
          <select
            value={formData.department_uuid}
            onChange={(e) => handleChange("department_uuid", e.target.value)}
            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#147db5] focus:ring-2 focus:ring-[#147db5]/10"
          >
            <option value="">Select Department</option>
            {dbDepartments.map((d) => (
              <option key={d.uuid} value={d.uuid}>
                {d.name || d.department_name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Employment Type</label>
          <select
            value={formData.employment_type_uuid}
            onChange={(e) => handleChange("employment_type_uuid", e.target.value)}
            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#147db5] focus:ring-2 focus:ring-[#147db5]/10"
          >
            <option value="">Select Type</option>
            {dbEmploymentTypes.map((t) => (
              <option key={t.uuid} value={t.uuid}>
                {t.type_name || t.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Work Schedule</label>
          <select
            value={formData.work_schedule_uuid}
            onChange={(e) => handleChange("work_schedule_uuid", e.target.value)}
            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#147db5] focus:ring-2 focus:ring-[#147db5]/10"
          >
            <option value="">Select Schedule</option>
            {dbWorkSchedules.map((s) => (
              <option key={s.uuid} value={s.uuid}>
                {s.schedule_name || s.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Joining Date</label>
            <input
              type="date"
              value={formData.joining_date}
              onChange={(e) => handleChange("joining_date", e.target.value)}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-[#147db5] focus:ring-2 focus:ring-[#147db5]/10"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-600">Status</label>
            <select
              value={formData.status}
              onChange={(e) => handleChange("status", e.target.value)}
              className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#147db5] focus:ring-2 focus:ring-[#147db5]/10"
            >
              <option value="Active">Active</option>
              <option value="Draft">Draft</option>
              <option value="Terminated">Terminated</option>
              <option value="On Leave">On Leave</option>
            </select>
          </div>
        </div>
      </form>
    </RightDrawer>
  );
};

export default EditEmployeeDrawer;
